// src/pages/workflow/WorkflowContainer.js
import React, { useState, useEffect } from 'react';
import { Link as RouterLink, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { Box, Container, Alert, Button, Link, Stepper, Step, StepLabel } from '@mui/material';
import DashboardLayout from '../../layout/DashboardLayout';
import { useWorkflow } from '../../hooks/useWorkflow';
import { generateICPStandalone, personSearch } from '../../services/autoPilotService';
import { createListWithProfiles } from '../../services/userService';
import WorkflowStart from './WorkflowStart';
import ICPConfirmation from './ICPConfirmation';
import ICPReview from './ICPReview';
import WorkflowResults from './WorkflowResults';
import PersonSearchResults from '../../components/autopilot/PersonSearchResults';

/**
 * Generate a unique session ID
 * @returns {string} Session ID
 */
const generateSessionId = () => {
  return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Main workflow container component that manages the workflow state machine
 */
function WorkflowContainer() {
  const location = useLocation();
  const navigate = useNavigate();
  const userInfo = typeof window !== 'undefined' ? localStorage.getItem('userInfo') : null;
  const user = userInfo ? JSON.parse(userInfo) : null;
  const remainingCredits = user?.remainingCredits ?? 0;
  const [sessionId] = useState(() => {
    // Try to get from localStorage first
    const saved = localStorage.getItem('workflowSessionId');
    if (saved) return saved;
    // Generate new one
    const newId = generateSessionId();
    localStorage.setItem('workflowSessionId', newId);
    return newId;
  });

  // Local state for ICP generation step
  const [icpData, setIcpData] = useState(null);
  const [rephrase, setRephrase] = useState(null);
  const [icpLoading, setIcpLoading] = useState(false);
  const [icpError, setIcpError] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [searchResults, setSearchResults] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [createdListId, setCreatedListId] = useState(null);

  const {
    stage,
    category,
    icp,
    output,
    sourcingQueries,
    results,
    loading,
    error,
    approveWorkflow,
    rejectWorkflow,
    resetState,
  } = useWorkflow();

  // Clear session ID from localStorage when component unmounts or workflow resets
  useEffect(() => {
    return () => {
      // Optionally clear on unmount
      // localStorage.removeItem('workflowSessionId');
    };
  }, []);

  const handleStart = async (input) => {
    setIcpLoading(true);
    setIcpError(null);
    setShowConfirmation(false);
    setIcpData(null);
    setRephrase(null);

    try {
      // Call only the ICP generation API
      const response = await generateICPStandalone(input);
      const isSuccess = response?.success === true || response?.status === 'success';
      const responseData =
        response?.data?.data ||
        response?.data ||
        response;
      const hasIcpPayload =
        !!responseData &&
        (responseData.icp ||
          responseData.rephrase ||
          responseData.clusters_for_sourcing_pro);

      if ((isSuccess || hasIcpPayload) && hasIcpPayload) {
        setIcpData(responseData);
        setRephrase(responseData?.rephrase || null);
        setShowConfirmation(true);
        navigate('/workflow/confirm');
      } else {
        setIcpError(response?.message || 'Failed to generate ICP');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to generate ICP';
      setIcpError(errorMessage);
      console.error('Failed to generate ICP:', err);
    } finally {
      setIcpLoading(false);
    }
  };

  const handleConfirm = async (updatedClusters, resultsCount) => {
    const clusters = updatedClusters || icpData?.clusters_for_sourcing_pro;
    const sizePerQuery = resultsCount || 10;

    if (!icpData || !clusters) {
      setSearchError('ICP data is incomplete');
      return;
    }

    setSearchLoading(true);
    setSearchError(null);

    try {
      // Call the person-search API with clusters in the required format
      const response = await personSearch({
        clusters: {
          titles: clusters.titles || [],
          organisations: clusters.organisations || [],
          skills: clusters.skills || [],
          seniority: clusters.seniority || [],
          experience_year_patterns: clusters.experience_year_patterns || [],
          locations: clusters.locations || []
        },
        meta: {
          count: 3
        },
        size_per_query: sizePerQuery,
        titlecase: false,
        dataset: 'all',
        use_sandbox: true,
        concurrency: 3
      });

      const responseData = response?.data?.data || response?.data || response?.results || response;
      const normalizedResultsArray = Array.isArray(responseData)
        ? responseData
        : Array.isArray(responseData?.results)
          ? responseData.results
          : Array.isArray(responseData?.items)
            ? responseData.items
            : [];
      const isSearchSuccess =
        response?.success === true ||
        response?.status === 'success' ||
        Array.isArray(responseData) ||
        (Array.isArray(normalizedResultsArray) && normalizedResultsArray.length > 0);
      const normalizedSearchResponse = {
        ...response,
        success: isSearchSuccess,
        data: normalizedResultsArray,
      };

      if (isSearchSuccess) {
        setSearchResults(normalizedSearchResponse);
        setShowConfirmation(false);
        setCreatedListId(null);

        // When person-search returns data, create a list with those profiles
        const data = normalizedSearchResponse.data;
        if (Array.isArray(data) && data.length > 0) {
          const linkedinUsernames = [];
          const seen = new Set();
          for (const profile of data) {
            let username = null;
            if (profile.socialProfiles?.length) {
              const linkedin = profile.socialProfiles.find((p) => p.network === 'linkedin');
              if (linkedin?.username) username = linkedin.username;
            }
            if (!username && profile.linkedinLink) {
              const match = profile.linkedinLink.match(/linkedin\.com\/in\/([^/?]+)/i);
              if (match) username = match[1];
            }
            if (username && !seen.has(username)) {
              seen.add(username);
              linkedinUsernames.push(username);
            }
          }
          if (linkedinUsernames.length > 0) {
            try {
              const listName = icpData?.rephrase
                ? `Person search: ${String(icpData.rephrase).slice(0, 80)}${String(icpData.rephrase).length > 80 ? '…' : ''}`
                : `Person search - ${new Date().toLocaleString()}`;
              const listResponse = await createListWithProfiles({
                name: listName,
                description: '',
                linkedinUsernames
              });
              const listId = listResponse?.data?._id ?? listResponse?.list?._id ?? listResponse?._id;
              if (listId) {
                setCreatedListId(listId);
              }
            } catch (listErr) {
              console.error('Failed to create list with profiles:', listErr);
            }
          }
        }
      } else {
        setSearchError(response?.message || 'Failed to search for persons');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to search for persons';
      setSearchError(errorMessage);
      console.error('Failed to search for persons:', err);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleCancel = () => {
    setShowConfirmation(false);
    setIcpData(null);
    setRephrase(null);
    navigate('/workflow/start');
  };

  const handleApprove = async () => {
    try {
      await approveWorkflow(sessionId);
    } catch (err) {
      console.error('Failed to approve workflow:', err);
      
      // Handle session expiration
      if (err.message && err.message.includes('Session expired')) {
        alert('Your session has expired. Please start a new workflow.');
        resetState();
      }
    }
  };

  const handleReject = async (feedback) => {
    try {
      await rejectWorkflow(sessionId, feedback);
    } catch (err) {
      console.error('Failed to reject workflow:', err);
      
      // Handle session expiration
      if (err.message && err.message.includes('Session expired')) {
        alert('Your session has expired. Please start a new workflow.');
        resetState();
      }
    }
  };

  const handleReset = () => {
    resetState();
    setIcpData(null);
    setRephrase(null);
    setShowConfirmation(false);
    setIcpError(null);
    setSearchResults(null);
    setSearchError(null);
    setCreatedListId(null);
    // Generate new session ID
    const newSessionId = generateSessionId();
    localStorage.setItem('workflowSessionId', newSessionId);
    navigate('/workflow/start');
  };

  useEffect(() => {
    if (stage === 'awaiting_approval' && category === 'Getlista Auto Pilot') {
      navigate('/workflow/review');
    }
    if (stage === 'done') {
      navigate('/workflow/results');
    }
  }, [stage, category, navigate]);

  useEffect(() => {
    if (searchResults?.success) {
      navigate('/workflow/results');
    }
  }, [searchResults, navigate]);

  const workflowSteps = [
    { key: 'start', label: 'Start', path: '/workflow/start' },
    { key: 'confirm', label: 'Confirm ICP', path: '/workflow/confirm' },
    { key: 'review', label: 'Review', path: '/workflow/review' },
    { key: 'results', label: 'Results', path: '/workflow/results' },
  ];

  const currentStepIndex = Math.max(
    workflowSteps.findIndex((step) => location.pathname.startsWith(step.path)),
    0
  );

  return (
    <DashboardLayout pageName="Workflow">
      <Container
        maxWidth={searchResults?.success ? false : 'xl'}
        disableGutters={!!searchResults?.success}
        sx={{ py: 0, width: '100%' }}
      >
        <Box sx={{ pt: 2, pb: 1, px: 2 }}>
          <Stepper activeStep={currentStepIndex} alternativeLabel>
            {workflowSteps.map((step) => (
              <Step key={step.key}>
                <StepLabel>{step.label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        {searchError && (
          <Alert severity="error" sx={{ mb: 3, maxWidth: 1000, mx: 'auto' }}>
            {searchError}
          </Alert>
        )}

        <Routes>
          <Route
            path="/"
            element={<Navigate to="/workflow/start" replace />}
          />
          <Route
            path="start"
            element={
              <WorkflowStart
                onStart={handleStart}
                loading={icpLoading}
                error={icpError}
              />
            }
          />
          <Route
            path="confirm"
            element={
              showConfirmation && icpData && !searchResults ? (
                <ICPConfirmation
                  icpData={icpData}
                  onConfirm={handleConfirm}
                  onCancel={handleCancel}
                  loading={searchLoading || loading}
                  remainingCredits={remainingCredits}
                />
              ) : (
                <Navigate to="/workflow/start" replace />
              )
            }
          />
          <Route
            path="review"
            element={
              stage === 'awaiting_approval' && category === 'Getlista Auto Pilot' ? (
                <ICPReview
                  icpString={icp}
                  onApprove={handleApprove}
                  onReject={handleReject}
                  loading={loading}
                  error={error}
                />
              ) : (
                <Navigate to="/workflow/start" replace />
              )
            }
          />
          <Route
            path="results"
            element={
              searchResults && searchResults.success ? (
                <Box sx={{ mb: 4, marginLeft: -2, marginRight: -2, width: 'calc(100% + 32px)', maxWidth: 'none', boxSizing: 'border-box' }}>
                  <PersonSearchResults searchResults={searchResults} />
                  {createdListId && (
                    <Alert severity="success" sx={{ mb: 2, maxWidth: 600, mx: 'auto' }}>
                      Profiles saved to a list.{' '}
                      <Link component={RouterLink} to={`/list/${createdListId}`} underline="hover" sx={{ fontWeight: 600 }}>
                        View list
                      </Link>
                    </Alert>
                  )}
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                    <Button
                      variant="contained"
                      onClick={handleReset}
                      sx={{
                        bgcolor: '#1BE885',
                        color: '#000',
                        fontWeight: 600,
                        fontSize: '1rem',
                        '&:hover': {
                          bgcolor: '#20ec84',
                          transform: 'translateY(-2px)',
                          boxShadow: 3
                        },
                        transition: 'all 0.2s ease-in-out'
                      }}
                    >
                      Start New Search
                    </Button>
                  </Box>
                </Box>
              ) : stage === 'done' ? (
                <WorkflowResults
                  category={category}
                  sourcingQueries={sourcingQueries}
                  results={results}
                  output={output}
                  onReset={handleReset}
                />
              ) : searchLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
                  <Alert severity="info">Loading search results...</Alert>
                </Box>
              ) : (
                <Navigate to="/workflow/start" replace />
              )
            }
          />
          <Route path="*" element={<Navigate to="/workflow/start" replace />} />
        </Routes>
      </Container>
    </DashboardLayout>
  );
}

export default WorkflowContainer;
